import { useProjects, useTasks } from "@hq/client-react";
import { projectView, projectProgress } from "@hq/core";
import type { Project } from "@hq/contracts";
import { ProjectAreaSelect } from "../ProjectAreaSelect";
import { ProjectProgress } from "../ProjectProgress";
import { ProjectTitle } from "../ProjectTitle";
import { cn } from "@z/ui";
import { QuickAdd } from "../QuickAdd";
import { TaskList } from "../TaskList";
import { ViewHeader } from "../ViewHeader";
import { type ViewProps, viewClassName } from "./shared";
import { useViewState } from "./useViewState";

export interface ProjectTasksViewProps extends ViewProps {
  projectId: string;
}

/** One project's tasks grouped by heading, with quick add defaulting to the project. */
export function ProjectTasksView({ projectId, onOpenTask, now, className }: ProjectTasksViewProps) {
  const { data: projects } = useProjects();
  const project = projects?.find((p) => p.id === projectId);
  return (
    <ProjectTasksBody
      key={projectId}
      projectId={projectId}
      {...(project ? { project } : {})}
      {...(onOpenTask ? { onOpenTask } : {})}
      {...(now ? { now } : {})}
      {...(className !== undefined ? { className } : {})}
    />
  );
}

/** Keyed on the project so display overrides reset when the pane switches project. */
function ProjectTasksBody({
  projectId,
  project,
  onOpenTask,
  now,
  className,
}: ProjectTasksViewProps & { project?: Project }) {
  const name = project?.name ?? "Project";
  const { data: tasks = [] } = useTasks({ projectId });
  const count = projectProgress(tasks).get(projectId) ?? { completed: 0, total: 0 };
  const s = useViewState(projectView(projectId, name));
  return (
    <div data-slot="project-tasks-view" className={cn(viewClassName, className)}>
      <ViewHeader
        mode={s.mode}
        onModeChange={s.setMode}
        title={
          <span className="inline-flex min-w-0 items-center gap-2">
            <ProjectProgress {...count} className="size-5 shrink-0 text-fg-muted" />
            {project ? <ProjectTitle project={project} /> : name}
          </span>
        }
        titleMetadata={
          <span className="text-xs text-fg-muted tabular-nums" role="status">
            {count.total === 0 ? "No todos yet" : `${count.completed} of ${count.total} completed`}
          </span>
        }
        actions={project && <ProjectAreaSelect project={project} />}
        sort={s.sort}
        onSortChange={s.setSort}
        tags={s.tags}
        onTagsChange={s.setTags}
        availableTags={s.hasTasks ? s.allTags : []}
        displayControls={s.hasTasks && s.mode === "list"}
        groupBy={s.groupBy}
        onGroupByChange={s.setGroupBy}
        showCompleted={s.showCompleted}
        onShowCompletedChange={s.setShowCompleted}
      />
      <TaskList
        view={s.view}
        mode={s.mode}
        onTagClick={s.toggleTag}
        projectId={projectId}
        quickAdd={
          <QuickAdd
            projectId={projectId}
            {...(now ? { now } : {})}
            placeholder={`Add a task to ${name}…`}
          />
        }
        emptyTitle="No tasks in this project"
        emptyDescription="Add one above; it lands in this project."
        {...(onOpenTask ? { onOpenTask } : {})}
        {...(now ? { now } : {})}
      />
    </div>
  );
}
